import { useCallback, useMemo } from 'react'
import { useMutation } from '@tanstack/react-query'

import { QUERY_KEYS as DOCUMENT_PAGES_QUERY_KEYS } from '@/services/queries/document_pages'
import MUTATION_KEYS from './mutationkeys'
import { FlattenedDocumentPage } from '@/lib/api/client'
import {
  Position,
  useListTemplateAttributes,
} from '@/services/hooks/template_attributes'
import queryClient from '@/queryClient'
import { getRelativeY, getSubPageIndexFromAbsoluteY, isLine } from './utils'
import { DEFAULT_LINE_HEIGHT, DEFAULT_STROKE_COLOR } from './constants'
import { LETTER_PAGE_HEIGHT } from '../DocumentTemplate'
import {
  useCreateTemplateAttribute,
  useUpdateTemplateAttribute,
} from '@/services/queries/template_attributes'
import { useDocumentState } from '@/state'

export const useSaveDraggableElement = () => {
  const { mutateAsync: createTemplate } = useCreateTemplateAttribute()
  const newElementData = useDocumentState((state) => state.newElementData)
  const clearNewElementData = useDocumentState(
    (state) => state.clearNewElementData,
  )

  return useMutation({
    mutationKey: [MUTATION_KEYS.SAVE_ANNOTATION],
    mutationFn: async ({
      documentPage,
      position,
    }: {
      documentPage: FlattenedDocumentPage
      position: { x: number; y: number }
    }) => {
      const dataType = newElementData?.dataType

      if (!dataType) return

      const attributes = newElementData?.attributes
      const type = dataType === 'annotation' ? attributes?.type : dataType
      const strokeColor = attributes?.strokeColor as string
      let newElementPayload = {} as any

      if (dataType === 'annotation') {
        newElementPayload.type = type
        newElementPayload.strokeColor = strokeColor
      }

      const documentSubPages =
        (documentPage?.template_values?.subPages as Array<string>) ?? []
      const { x, y } = position

      if (isLine(type)) {
        const startPoint = newElementData?.attributes.startPoint as Position
        const endPoint = { x, y }

        const absoluteStartY = startPoint.y
        const subPageIndex = getSubPageIndexFromAbsoluteY(absoluteStartY)
        const subPageId = documentSubPages[subPageIndex] ?? documentPage?.id

        const subPageStartOffset =
          subPageIndex === 0
            ? 0
            : LETTER_PAGE_HEIGHT - 64 + (subPageIndex - 1) * LETTER_PAGE_HEIGHT

        const relativeStartY = absoluteStartY - subPageStartOffset
        const relativeEndY = endPoint.y - subPageStartOffset

        newElementPayload.subPageId = subPageId
        newElementPayload.startPoint = {
          x: startPoint.x,
          y: relativeStartY,
        }

        newElementPayload.endPoint = {
          x: endPoint.x,
          y: relativeEndY,
        }

        newElementPayload.position = {
          x: startPoint.x - 5,
          y: relativeStartY - DEFAULT_LINE_HEIGHT - 2,
        }

        if (type.includes('bubble')) {
          newElementPayload['bubbleValue'] =
            newElementData?.attributes.bubbleValue
        }
      } else {
        newElementPayload = {
          ...attributes,
        }
        const newSubPageIndex = getSubPageIndexFromAbsoluteY(y)
        const newSubPageId =
          documentSubPages[newSubPageIndex] ?? documentPage?.id
        const newRelativeY = getRelativeY(y)
        newElementPayload['position'] = {
          x,
          y: newRelativeY,
        }
        newElementPayload.subPageId = newSubPageId
      }

      if (newElementPayload.strokeColor === DEFAULT_STROKE_COLOR) {
        delete newElementPayload.strokeColor
      }

      clearNewElementData()

      try {
        await createTemplate({
          payload: {
            data_type: dataType,
            template_values: newElementPayload,
          },
          documentPageId: documentPage.id as string,
        })
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
      } catch (_e) {
        throw new Error('Failed to create template attribute')
      }
    },
    onSettled: async () => {
      await queryClient.invalidateQueries({
        queryKey: [DOCUMENT_PAGES_QUERY_KEYS.DOCUMENT_PAGES],
      })
    },
  })
}

export const useAnnotationLayer = (documentPageId: string) => {
  const { mutate: updateTemplate, isPending: isUpdatingTemplate } =
    useUpdateTemplateAttribute({})
  const { data: templates, isLoading } = useListTemplateAttributes({
    documentPageId,
  })

  const zValues = useMemo(
    () => templates?.map((t) => (t.template_values.position as any)?.z || 1000),
    [templates],
  )

  const maxZIndex = useMemo(() => {
    if (zValues?.length === 0) return 0
    return Math.max(...(zValues || []))
  }, [zValues])

  const minZIndex = useMemo(() => {
    if (zValues?.length === 0) return 1
    return Math.min(...(zValues || []))
  }, [zValues])

  const bringForward = useCallback(
    (templateId: string) => {
      const template = templates?.find((a) => a.id === templateId)
      updateTemplate({
        templateAttributeId: templateId,
        documentPageId,
        payload: {
          data_type: template?.data_type,
          template_values: {
            ...template?.template_values,
            position: {
              ...(template?.template_values?.position || {}),
              z: ((template?.template_values?.position as any)?.z || 1) + 1,
            },
          },
        } as any,
      })
    },
    [templates, updateTemplate],
  )

  const bringToFront = useCallback(
    (templateId: string) => {
      const template = templates?.find((a) => a.id === templateId)
      updateTemplate({
        templateAttributeId: templateId,
        documentPageId,
        payload: {
          data_type: template?.data_type,
          template_values: {
            ...template?.template_values,
            position: {
              ...(template?.template_values?.position || {}),
              z: maxZIndex + 1,
            },
          },
        } as any,
      })
    },
    [templates, maxZIndex, updateTemplate],
  )

  const sendBackward = useCallback(
    (templateId: string) => {
      const template = templates?.find((a) => a.id === templateId)
      updateTemplate({
        templateAttributeId: templateId,
        documentPageId,
        payload: {
          data_type: template?.data_type,
          template_values: {
            ...template?.template_values,
            position: {
              ...(template?.template_values?.position || {}),
              z: ((template?.template_values?.position as any)?.z || 1) - 1,
            },
          },
        } as any,
      })
    },
    [templates, updateTemplate],
  )

  const sendToBack = useCallback(
    (templateId: string) => {
      const template = templates?.find((a) => a.id === templateId)
      updateTemplate({
        templateAttributeId: templateId,
        documentPageId,
        payload: {
          data_type: template?.data_type,
          template_values: {
            ...template?.template_values,
            position: {
              ...(template?.template_values?.position || {}),
              z: Math.max(minZIndex - 1, 0),
            },
          },
        } as any,
      })
    },
    [templates, minZIndex, updateTemplate],
  )

  return {
    bringForward,
    bringToFront,
    sendBackward,
    sendToBack,
    isLoading: isLoading || isUpdatingTemplate,
    maxZIndex,
  }
}
